import { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { BsListNested, BsPencil } from 'react-icons/bs';

import Checkbox from '#globalComponents/Checkbox/Checkbox';
import Select from '#globalComponents/Forms/Select';
import Textarea from '#globalComponents/Forms/Textarea';
import TextInput from '#globalComponents/Forms/TextInput';

import { useCustomFieldDefs, useVisibleFieldDefs } from '../../../models/customFields/queries';
import { validateField } from '../../../utils/fieldValidation';
import { parseFieldValue } from '../../../utils/fieldValues';

import CustomFieldInput from './CustomFieldInput';

/**
 * Renders fields for an entity (company/contact/deal).
 * Supports both default (built-in) fields and custom fields in a unified view.
 *
 * Props:
 *   entityType    — 'company' | 'contact' | 'deal'
 *   values        — current customFields JSONB object (e.g. { "fieldId": "value" })
 *   onSave        — called with the full updated customFields object (for custom fields)
 *   readOnly      — if true, renders as static kv-pairs (no click-to-edit)
 *   inline        — controlled form mode for create flows
 *   onChange       — required when inline=true, called as onChange(fieldId, value) for custom
 *                   or onChange(fieldKey, value) for default fields
 *   entity        — the full entity object (for reading default field values)
 *   onSaveEntity  — callback for default field changes: (fieldKey, value)
 *   onChangeEntity — for inline mode with default fields: (fieldKey, value)
 *   showLabel     — show section header (default true)
 */
const CustomFieldsSection = ({
	entityType,
	values = {},
	onSave,
	readOnly = false,
	inline = false,
	onChange,
	entity = null,
	onSaveEntity,
	onChangeEntity,
	showLabel = true,
}) => {
	// Use visible fields when entity is provided (unified mode), otherwise use all custom defs
	const unified = Boolean(entity) || Boolean(onChangeEntity);
	const { data: visibleDefs = [] } = useVisibleFieldDefs(entityType);
	const { data: allDefs = [] } = useCustomFieldDefs(entityType);

	const fieldDefs = unified
		? visibleDefs
		: allDefs.filter((f) => !f.isDefault).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

	if (fieldDefs.length === 0) return null;

	// Helper to get value for a field
	const getValue = (def) => {
		if (def.isDefault) {
			if (entity) return entity[def.fieldKey] ?? (def.fieldType === 'checkbox' ? false : '');
			return '';
		}
		return values[def.id] ?? (def.fieldType === 'checkbox' ? false : '');
	};

	// Helper to handle change for a field
	const handleInlineChange = (def, val) => {
		if (def.isDefault) {
			if (onChangeEntity) onChangeEntity(def.fieldKey, val);
		} else {
			if (onChange) onChange(def.id, val);
		}
	};

	const handleSave = (def, val) => {
		if (def.isDefault) {
			if (onSaveEntity) onSaveEntity(def.fieldKey, val);
		} else {
			if (onSave) onSave({ ...values, [def.id]: val });
		}
	};

	// Inline mode — controlled form inputs for create flows
	if (inline) {
		return (
			<div className="space-y-1.5">
				{showLabel && (
					<div className="text-[10px] font-semibold uppercase tracking-wider text-bgBase-800 flex items-center gap-1.5 pt-1">
						<BsListNested className="w-3 h-3" /> {unified ? 'Felder' : 'Eigene Felder'}
					</div>
				)}
				{fieldDefs.map((def) => (
					<InlineFieldInput
						key={def.id}
						def={def}
						value={getValue(def)}
						onChange={(val) => handleInlineChange(def, val)}
					/>
				))}
			</div>
		);
	}

	return (
		<div>
			{!readOnly && showLabel && (
				<h3 className="text-xs font-semibold uppercase tracking-wider text-bgBase-800 mb-2 flex items-center gap-2">
					<BsListNested className="w-3.5 h-3.5" /> {unified ? 'Felder' : 'Eigene Felder'}
				</h3>
			)}
			<div className="space-y-1">
				{fieldDefs.map((def) => (
					<CustomFieldRow
						key={def.id}
						def={def}
						value={getValue(def)}
						onSave={(val) => handleSave(def, val)}
						readOnly={readOnly}
					/>
				))}
			</div>
		</div>
	);
};

CustomFieldsSection.propTypes = {
	entityType: PropTypes.string.isRequired,
	values: PropTypes.object,
	onSave: PropTypes.func,
	readOnly: PropTypes.bool,
	inline: PropTypes.bool,
	onChange: PropTypes.func,
	entity: PropTypes.object,
	onSaveEntity: PropTypes.func,
	onChangeEntity: PropTypes.func,
	showLabel: PropTypes.bool,
};

const InlineFieldInput = ({ def, value, onChange }) => {
	const label = def.name + (def.isRequired ? ' *' : '');
	const [error, setError] = useState(null);

	const handleChange = (val) => {
		if (error) setError(null);
		onChange(val);
	};

	const handleBlur = () => {
		const result = validateField(def, value);
		if (!result.valid) {
			setError(result.error);
		} else if (result.normalized && result.normalized !== value) {
			onChange(result.normalized);
		}
	};

	if (def.fieldType === 'checkbox') {
		return (
			<div className="flex items-center h-[34px]">
				<Checkbox
					id={`custom-field-${def.id}`}
					checked={Boolean(value)}
					onChange={onChange}
					label={
						<>
							{def.name}
							{def.isRequired && <span className="text-warning-500 text-xs ml-1">*</span>}
						</>
					}
				/>
			</div>
		);
	}

	if (def.fieldType === 'select') {
		return (
			<div>
				<span className="block text-[11px] font-medium text-bgBase-800 mb-1">
					{def.name}
					{def.isRequired && <span className="text-warning-500 ml-0.5">*</span>}
				</span>
				<Select value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
					<option value="">Auswählen …</option>
					{(def.selectOptions ?? []).map((opt) => (
						<option key={opt} value={opt}>
							{opt}
						</option>
					))}
				</Select>
			</div>
		);
	}

	if (def.fieldType === 'textarea') {
		return (
			<div>
				<Textarea
					value={value ?? ''}
					onChange={(e) => handleChange(e.target.value)}
					onBlur={handleBlur}
					placeholder={label}
					rows={2}
					error={Boolean(error)}
				/>
				{error && <p className="text-[11px] text-warning-500 mt-0.5">{error}</p>}
			</div>
		);
	}

	if (def.fieldType === 'date') {
		return (
			<div>
				<span className="block text-[11px] font-medium text-bgBase-800 mb-1">
					{def.name}
					{def.isRequired && <span className="text-warning-500 ml-0.5">*</span>}
				</span>
				<TextInput
					type="date"
					value={value ?? ''}
					onChange={(e) => handleChange(e.target.value)}
					onBlur={handleBlur}
					error={Boolean(error)}
				/>
				{error && <p className="text-[11px] text-warning-500 mt-0.5">{error}</p>}
			</div>
		);
	}

	return (
		<div>
			<TextInput
				type={def.fieldType === 'number' ? 'number' : 'text'}
				value={value ?? ''}
				onChange={(e) => handleChange(e.target.value)}
				onBlur={handleBlur}
				placeholder={label}
				error={Boolean(error)}
			/>
			{error && <p className="text-[11px] text-warning-500 mt-0.5">{error}</p>}
		</div>
	);
};

InlineFieldInput.propTypes = {
	def: PropTypes.object.isRequired,
	value: PropTypes.any,
	onChange: PropTypes.func.isRequired,
};

const formatDisplayValue = (fieldType, value) => {
	if (value === null || value === undefined || value === '') return null;
	if (fieldType === 'checkbox') return value ? 'Ja' : 'Nein';
	if (fieldType === 'date') {
		try {
			return new Intl.DateTimeFormat('de-DE').format(new Date(value));
		} catch {
			return String(value);
		}
	}
	return String(value);
};

const FieldDisplayValue = ({ fieldType, value, displayValue, onEdit }) => {
	if (fieldType === 'url' && displayValue) {
		return (
			<div className="flex-1 flex items-center gap-1.5 min-w-0">
				<a
					href={value.startsWith('http') ? value : `https://${value}`}
					target="_blank"
					rel="noopener noreferrer"
					className="text-sm text-primary-600 hover:text-primary-700 hover:underline truncate"
				>
					{displayValue}
				</a>
				<button type="button" onClick={onEdit} className="text-bgBase-800 hover:text-text flex-shrink-0 p-0.5">
					<BsPencil className="w-2.5 h-2.5" />
				</button>
			</div>
		);
	}

	return (
		<button
			type="button"
			onClick={onEdit}
			className={`flex-1 text-left text-sm truncate px-0 py-0.5 rounded-control hover:bg-bgBase-300 transition-colors ${
				displayValue ? 'text-text' : 'text-bgBase-800 italic'
			}`}
		>
			{displayValue || 'Eingeben …'}
		</button>
	);
};

FieldDisplayValue.propTypes = {
	fieldType: PropTypes.string.isRequired,
	value: PropTypes.any,
	displayValue: PropTypes.string,
	onEdit: PropTypes.func.isRequired,
};

const CustomFieldRow = ({ def, value, onSave, readOnly = false }) => {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState(value ?? '');
	const [error, setError] = useState(null);

	useEffect(() => {
		setDraft(value ?? '');
	}, [value]);

	const commit = () => {
		const parsed = parseFieldValue(def.fieldType, draft);
		const result = validateField(def, parsed);
		if (!result.valid) {
			setError(result.error);
			return;
		}
		const final = result.normalized ?? parsed;
		setEditing(false);
		setError(null);
		if (final !== value) onSave(final);
	};

	const dv = formatDisplayValue(def.fieldType, value);

	// Read-only mode — static kv pair
	if (readOnly) {
		return (
			<div className="flex justify-between gap-3 py-[5px] text-xs min-w-0">
				<span className="text-bgBase-800 flex-shrink-0">{def.name}</span>
				{def.fieldType === 'url' && dv ? (
					<a
						href={dv.startsWith('http') ? dv : `https://${dv}`}
						target="_blank"
						rel="noopener noreferrer"
						className="text-primary-600 hover:text-primary-700 font-medium truncate hover:underline"
					>
						{dv}
					</a>
				) : (
					<span className="text-text font-medium truncate">{dv || '–'}</span>
				)}
			</div>
		);
	}

	// Checkbox — no edit mode, just toggle
	if (def.fieldType === 'checkbox') {
		return (
			<div className="flex items-center min-h-[28px]">
				<Checkbox
					id={`custom-field-row-${def.id}`}
					checked={Boolean(value)}
					onChange={onSave}
					label={def.name}
				/>
			</div>
		);
	}

	const handleDraftChange = (val) => {
		if (error) setError(null);
		setDraft(val);
	};

	const handleCancel = () => {
		setDraft(value ?? '');
		setError(null);
		setEditing(false);
	};

	return (
		<div className="flex items-start gap-2 min-h-[28px]">
			<span className="text-xs text-bgBase-800 w-24 flex-shrink-0 truncate pt-1" title={def.name}>
				{def.name}
			</span>
			{editing ? (
				<div className="flex-1 min-w-0">
					<CustomFieldInput
						def={def}
						value={draft}
						onChange={handleDraftChange}
						onBlur={commit}
						onKeyDown={(e) => {
							if (e.key === 'Enter') commit();
							if (e.key === 'Escape') handleCancel();
						}}
						error={Boolean(error)}
					/>
					{error && <p className="text-[11px] text-warning-500 mt-0.5">{error}</p>}
				</div>
			) : (
				<FieldDisplayValue
					fieldType={def.fieldType}
					value={value}
					displayValue={dv}
					onEdit={() => setEditing(true)}
				/>
			)}
		</div>
	);
};

CustomFieldRow.propTypes = {
	def: PropTypes.object.isRequired,
	value: PropTypes.any,
	onSave: PropTypes.func.isRequired,
	readOnly: PropTypes.bool,
};

export default CustomFieldsSection;
